import { useCallback, useState, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import { useAssetActions } from 'asset-actions-lib/context/AssetActionsContext';
import FormsCampaignSelect from 'asset-actions-lib-forms/selectCampaign/FormsCampaignSelect';
import { useAssociateCampaign } from 'asset-actions-lib-forms/selectCampaign/hooks/useAssociateCampaign';
import { FORM_ACTION_IDS } from 'asset-actions-lib-forms/constants/actionIds';
import usePrevious from 'react-utils/hooks/usePrevious';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CAMPAIGNS_ACCESS = 'campaigns-access';
const CAMPAIGNS_WRITE = 'campaigns-write';
export default function SelectCampaignModal({
  onClose,
  onConfirm,
  scopes
}) {
  const {
    crmObject,
    onActionError,
    nonCrmObject
  } = useAssetActions();
  const optimisticCampaignUpdate = nonCrmObject === null || nonCrmObject === void 0 ? void 0 : nonCrmObject.optimisticCampaignUpdate;
  const hasCampaignsReadAccess = useMemo(() => scopes.includes(CAMPAIGNS_ACCESS), [scopes]);
  const hasCampaignsWriteAccess = useMemo(() => scopes.includes(CAMPAIGNS_WRITE), [scopes]);
  const formGuid = useMemo(() => getPropertyValue(crmObject, 'hs_form_id'), [crmObject]);
  const formCrmObjectId = useMemo(() => getPropertyValue(crmObject, 'hs_object_id'), [crmObject]);
  const [selectedCampaignGuid, setSelectedCampaignGuid] = useState(null);
  const previousCampaignGuid = usePrevious(selectedCampaignGuid);
  const {
    associateCampaign,
    loading,
    error: associateError
  } = useAssociateCampaign({
    formCrmObjectId,
    campaignGuid: selectedCampaignGuid || ''
  });
  const onSaveCampaign = useCallback(() => {
    associateCampaign({
      variables: {
        formCrmObjectId,
        campaignGuid: selectedCampaignGuid || previousCampaignGuid,
        // @ts-expect-error: deleteAssociation is defined in mutation but not in function parameters
        deleteAssociation: !selectedCampaignGuid
      }
    }).then(() => {
      onConfirm(FORM_ACTION_IDS.SELECT_CAMPAIGN);
      if (optimisticCampaignUpdate) {
        optimisticCampaignUpdate(selectedCampaignGuid, formGuid);
      }
      onClose();
      if (associateError) {
        if (onActionError) {
          onActionError({
            actionId: FORM_ACTION_IDS.SELECT_CAMPAIGN,
            data: associateError
          });
        }
      }
    }).catch(() => {
      onClose();
      if (onActionError) {
        onActionError({
          actionId: FORM_ACTION_IDS.SELECT_CAMPAIGN,
          data: associateError
        });
      }
    });
  }, [associateCampaign, associateError, formCrmObjectId, onClose, onConfirm, onActionError, previousCampaignGuid, selectedCampaignGuid, optimisticCampaignUpdate, formGuid]);
  const onChangeCampaign = useCallback(campaignId => {
    setSelectedCampaignGuid(campaignId);
  }, []);
  return /*#__PURE__*/_jsxs(Modal, {
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "assetActionsLibForms.modals.associateCampaign.header"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(FormsCampaignSelect, {
        formGuid: formGuid,
        onChange: onChangeCampaign,
        hasCampaignsReadAccess: hasCampaignsReadAccess,
        hasCampaignsWriteAccess: hasCampaignsWriteAccess,
        selectedCampaignGuid: selectedCampaignGuid
      }), /*#__PURE__*/_jsx(Alert, {
        type: "info",
        marginBlockStart: "-500",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          "data-test-id": "details-edit-campaign-warning",
          message: "campaignSelect.details"
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        use: "primary",
        onClick: onSaveCampaign,
        loading: loading,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "assetActionsLibForms.modals.associateCampaign.saveButton"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "assetActionsLib.modals.shared.cancelButton"
        })
      })]
    })]
  });
}